import { useLocation } from 'react-router-dom';
import { Formik } from 'formik';
import { noop } from 'lodash';

import EditOffIcon from '@mui/icons-material/EditOff';
import EditIcon from '@mui/icons-material/Edit';

import {
  getFragmentData,
  graphql,
  ResolvableModeEnum,
} from '@e-socna/data-layer';
import type { FragmentType } from '@e-socna/data-layer';

import { ServiceUsagesGroups } from '../../components/ServiceUsages/Groups';
import { SpeedDial } from '../../components/SpeedDial';
import { ResolvableMissionTypeServiceUsagesFormStep } from '../../forms/ResolvableMissionType/steps/ServiceUsages';
import { ResolvableMissionTypeFormTab } from '../../forms/ResolvableMissionType';
import type { ResolvableServiceUsagePaths } from '../../forms/ResolvableServiceUser/types/paths.type';

export const ResolvableMissionTypePage_ServiceUsages_ResolvableMissionType_Fragment =
  graphql(`
    fragment ResolvableMissionTypePage_ServiceUsages_ResolvableMissionType_Fragment on ResolvableMissionType {
      _id

      serviceUsagesGroups {
        _id
        index
        groupingMode
        isOptional
        label
        manualTotal {
          ...ManualTotalForm_ResolvableServiceValue_Fragment
        }
        serviceUsages {
          ...ServiceUsageCardCmp_ResolvableServiceUsage_Fragment
        }
        totalMode
      }

      serviceUsagesGroupTotalCalculations(businessContext: {}) {
        ...ServiceUsagesTotalCardCmp_GroupTotalCalculation_Fragment
      }

      totalCalculation(businessContext: {}) {
        ...ServiceUsagesTotalCardCmp_TotalCalculation_Fragment
      }

      ...ResolvableMissionTypeForm_ResolvableMissionType_Fragment
    }
  `);

interface Props {
  readonly paths?: ResolvableServiceUsagePaths;
  readonly resolvableMissionType: FragmentType<
    typeof ResolvableMissionTypePage_ServiceUsages_ResolvableMissionType_Fragment
  >;
}

export const ServiceUsages = ({
  paths,
  resolvableMissionType: pResolvableMissionType,
}: Readonly<Props>) => {
  const resolvableMissionType = getFragmentData(
    ResolvableMissionTypePage_ServiceUsages_ResolvableMissionType_Fragment,
    pResolvableMissionType,
  );

  const {
    serviceUsagesGroupTotalCalculations,
    serviceUsagesGroups,
    totalCalculation,
  } = resolvableMissionType;

  const { pathname: currentPath } = useLocation();

  const isInEditionMode = paths && currentPath !== paths.consult;

  return (
    <>
      {!isInEditionMode ? (
        <ServiceUsagesGroups
          consultResolvableServiceInNewTab={false}
          editionMode={false}
          groupTotalCalculations={serviceUsagesGroupTotalCalculations}
          isInResolvableMode
          resolvableFieldSettings={{
            enableDirectGeneration: false,
            primaryMode: ResolvableModeEnum.Static,
          }}
          serviceUsagesGroups={serviceUsagesGroups}
          totalCalculation={totalCalculation}
        />
      ) : (
        <Formik
          enableReinitialize
          initialValues={resolvableMissionType}
          onSubmit={noop}
        >
          <ResolvableMissionTypeServiceUsagesFormStep
            groupTotalCalculations={serviceUsagesGroupTotalCalculations}
            id={ResolvableMissionTypeFormTab.ServiceUsages}
            paths={paths}
            totalCalculation={totalCalculation}
            useDirectMutation
          />
        </Formik>
      )}
      {!!paths && (
        <SpeedDial
          actions={
            isInEditionMode
              ? [
                  {
                    id: 'leaveEditionMode',
                    icon: <EditOffIcon />,
                    name: 'Quitter Mode Édition',
                    to: paths.consult,
                  },
                ]
              : [
                  {
                    id: 'editResolvableMissionTypeServiceUsages',
                    icon: <EditIcon />,
                    name: 'Modifier Prestations',
                    to: paths.edit,
                  },
                ]
          }
          title="Actions possibles sur les Prestations"
        />
      )}
    </>
  );
};
